import React, { useEffect, useState, useRef } from "react";
import {
  Grid,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormHelperText,
  TextField,
  Paper,
  Typography,
  Tabs,
  Tab,
  Box,
  Divider,
  CircularProgress,
  InputAdornment,
  Tooltip,
  Button,
} from "@material-ui/core";
import InfoOutlinedIcon from "@material-ui/icons/InfoOutlined";
import { makeStyles } from "@material-ui/core/styles";
import useSettings from "../../hooks/useSettings";
import { toast } from "react-toastify";

const useStyles = makeStyles((theme) => ({
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    width: "100%",
    marginTop: theme.spacing(2),
  },
  tab: {
    backgroundColor: theme.palette.options,
    borderRadius: 4,
    width: "100%",
    "& .MuiTab-wrapper": {
      color: theme.palette.fontecor,
    },
    "& .MuiTabs-flexContainer": {
      justifyContent: "center",
    },
  },
  selectContainer: {
    width: "100%",
    textAlign: "left",
  },
  sectionTitle: {
    fontWeight: 600,
    fontSize: "0.8rem",
    textTransform: "uppercase",
    letterSpacing: "0.08em",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
    marginTop: theme.spacing(1),
  },
  sectionDivider: {
    margin: `${theme.spacing(3)}px 0 ${theme.spacing(2)}px`,
  },
  integrationCard: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(2),
    borderRadius: theme.spacing(1),
  },
  integrationTitle: {
    fontWeight: 600,
    marginBottom: theme.spacing(0.5),
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
  },
  integrationDesc: {
    fontSize: "0.8rem",
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1.5),
    lineHeight: 1.5,
  },
  infoIcon: {
    fontSize: 16,
    color: theme.palette.text.disabled,
    cursor: "default",
    verticalAlign: "middle",
  },
}));

const DEFAULT_FEEDBACK_MESSAGE =
  `Olá {{name}}! Seu atendimento de *{{service}}* foi concluído. Como foi a sua experiência?\n\n` +
  `*1* - _Insatisfeito_\n*2* - _Satisfeito_\n*3* - _Muito Satisfeito_\n\n` +
  `Sua opinião é muito importante para nós! 🙏`;

// Opções de configuração
const OPTIONS = {
  userRating: [
    { value: "disabled", label: "Desabilitadas" },
    { value: "enabled", label: "Habilitadas" },
  ],
  scheduleType: [
    { value: "disabled", label: "Desabilitado" },
    { value: "queue", label: "Por fila" },
    { value: "company", label: "Por empresa" },
  ],
  call: [
    { value: "disabled", label: "Não aceitar" },
    { value: "enabled", label: "Aceitar" },
  ],
  CheckMsgIsGroup: [
    { value: "disabled", label: "Desativado" },
    { value: "enabled", label: "Ativado" },
  ],
  sendGreetingAccepted: [
    { value: "disabled", label: "Desabilitado" },
    { value: "enabled", label: "Habilitado" },
  ],
  sendMsgTransfTicket: [
    { value: "disabled", label: "Desabilitado" },
    { value: "enabled", label: "Habilitado" },
  ],
  appointmentFeedback: [
    { value: "disabled", label: "Desabilitado" },
    { value: "enabled", label: "Habilitado" },
  ],
  crmAutoOpportunity: [
    { value: "enabled", label: "Habilitado" },
    { value: "disabled", label: "Desabilitado" },
  ],
  crmOwnerVisibility: [
    { value: "disabled", label: "Todos veem todos" },
    { value: "enabled", label: "Por carteira" },
  ],
  crmBillingReminder: [
    { value: "disabled", label: "Desabilitada" },
    { value: "enabled", label: "Habilitada" },
  ],
  crmReactivation: [
    { value: "disabled", label: "Desabilitada" },
    { value: "enabled", label: "Habilitada" },
  ],
};

// Componente reutilizável de select com descrição
const SelectOption = ({ label, helperText, value, onChange, loading, options, className }) => (
  <FormControl className={className} fullWidth>
    <InputLabel>{label}</InputLabel>
    <Select value={value} onChange={(e) => onChange(e.target.value)}>
      {options.map((opt) => (
        <MenuItem key={opt.value} value={opt.value}>
          {opt.label}
        </MenuItem>
      ))}
    </Select>
    <FormHelperText>
      {loading ? "Atualizando..." : helperText}
    </FormHelperText>
  </FormControl>
);

// TextField com debounce para integrações
const DebouncedTextField = ({ value: externalValue, onSave, loading, label, helperText, ...props }) => {
  const [value, setValue] = useState(externalValue || "");
  const timerRef = useRef(null);

  useEffect(() => {
    setValue(externalValue || "");
  }, [externalValue]);

  const handleChange = (e) => {
    const newValue = e.target.value;
    setValue(newValue);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      onSave(newValue);
    }, 800);
  };

  return (
    <TextField
      {...props}
      label={label}
      value={value}
      onChange={handleChange}
      variant="outlined"
      margin="dense"
      fullWidth
      helperText={loading ? "Salvando..." : helperText}
      InputProps={{
        endAdornment: loading ? (
          <InputAdornment position="end">
            <CircularProgress size={16} />
          </InputAdornment>
        ) : null,
      }}
    />
  );
};

function TabPanel(props) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`options-tabpanel-${index}`}
      aria-labelledby={`options-tab-${index}`}
      {...other}
    >
      {value === index && <Box p={2}>{children}</Box>}
    </div>
  );
}

export default function Options({ settings, scheduleTypeChanged }) {
  const classes = useStyles();
  const { update } = useSettings();

  const [tabIndex, setTabIndex] = useState(0);
  const [settingsState, setSettingsState] = useState({});
  const [loading, setLoading] = useState({});

  useEffect(() => {
    if (Array.isArray(settings)) {
      const initial = {};
      settings.forEach((s) => {
        if (s.key) initial[s.key] = s.value;
      });
      setSettingsState(initial);

      if (typeof scheduleTypeChanged === "function") {
        scheduleTypeChanged(initial.scheduleType === "company");
      }
    }
  }, [settings]); // eslint-disable-line react-hooks/exhaustive-deps

  const updateSetting = async (key, value, loaderKey = key) => {
    setLoading((prev) => ({ ...prev, [loaderKey]: true }));
    try {
      await update({ key, value });
      setSettingsState((prev) => ({ ...prev, [key]: value }));
      toast.success("Configuração atualizada.");

      if (key === "scheduleType" && typeof scheduleTypeChanged === "function") {
        scheduleTypeChanged(value === "company");
      }
    } catch (err) {
      toast.error("Erro ao atualizar configuração.");
    } finally {
      setLoading((prev) => ({ ...prev, [loaderKey]: false }));
    }
  };

  return (
    <Paper className={classes.paper} variant="outlined">
      <Tabs
        value={tabIndex}
        onChange={(_, v) => setTabIndex(v)}
        indicatorColor="primary"
        textColor="primary"
        className={classes.tab}
      >
        <Tab label="Geral" />
        <Tab label="Integrações" />
      </Tabs>

      {/* Aba Geral */}
      <TabPanel value={tabIndex} index={0}>

        {/* Grupo: Atendimento */}
        <Typography className={classes.sectionTitle}>Atendimento</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <SelectOption
              className={classes.selectContainer}
              label="Avaliações"
              helperText="Permite que clientes avaliem o atendimento ao encerrar."
              value={settingsState.userRating || "disabled"}
              onChange={(value) => updateSetting("userRating", value)}
              loading={loading.userRating}
              options={OPTIONS.userRating}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectOption
              className={classes.selectContainer}
              label="Saudação ao aceitar ticket"
              helperText="Envia mensagem automática quando agente aceita o atendimento."
              value={settingsState.sendGreetingAccepted || "disabled"}
              onChange={(value) => updateSetting("sendGreetingAccepted", value)}
              loading={loading.sendGreetingAccepted}
              options={OPTIONS.sendGreetingAccepted}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectOption
              className={classes.selectContainer}
              label="Mensagem ao transferir ticket"
              helperText="Notifica o cliente quando o atendimento é transferido entre agentes."
              value={settingsState.sendMsgTransfTicket || "disabled"}
              onChange={(value) => updateSetting("sendMsgTransfTicket", value)}
              loading={loading.sendMsgTransfTicket}
              options={OPTIONS.sendMsgTransfTicket}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectOption
              className={classes.selectContainer}
              label="Aceitar chamadas de voz"
              helperText="Define se a plataforma aceita chamadas de voz via WhatsApp."
              value={settingsState.call || "disabled"}
              onChange={(value) => updateSetting("call", value)}
              loading={loading.call}
              options={OPTIONS.call}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectOption
              className={classes.selectContainer}
              label="CRM — Negócio automático"
              helperText="Conversa nova cria (ou vincula a) um negócio no funil do CRM automaticamente."
              value={settingsState.crmAutoOpportunity || "enabled"}
              onChange={(value) => updateSetting("crmAutoOpportunity", value)}
              loading={loading.crmAutoOpportunity}
              options={OPTIONS.crmAutoOpportunity}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectOption
              className={classes.selectContainer}
              label="CRM — Visibilidade de contatos"
              helperText="Por carteira: usuário comum só vê os próprios contatos e os sem responsável (admin vê tudo)."
              value={settingsState.crmOwnerVisibility || "disabled"}
              onChange={(value) => updateSetting("crmOwnerVisibility", value)}
              loading={loading.crmOwnerVisibility}
              options={OPTIONS.crmOwnerVisibility}
            />
          </Grid>
        </Grid>

        <Divider className={classes.sectionDivider} />

        {/* Grupo: CRM — Automações de envio */}
        <Typography className={classes.sectionTitle}>
          CRM — Automações de envio
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <SelectOption
              className={classes.selectContainer}
              label="Régua de cobrança"
              helperText="Lembra lançamentos a receber pendentes: 3 dias antes, no dia e 3 dias após o vencimento (job diário às 09:00)."
              value={settingsState.crmBillingReminder || "disabled"}
              onChange={(value) => updateSetting("crmBillingReminder", value)}
              loading={loading.crmBillingReminder}
              options={OPTIONS.crmBillingReminder}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <DebouncedTextField
              label="Mensagem da cobrança"
              value={settingsState.crmBillingReminderTemplate || ""}
              onSave={(value) => updateSetting("crmBillingReminderTemplate", value)}
              loading={loading.crmBillingReminderTemplate}
              helperText="Variáveis: {nome} {valor} {vencimento} {descricao} {quando}. Vazio usa o texto padrão."
              multiline
              rows={2}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectOption
              className={classes.selectContainer}
              label="Reativação de inativos"
              helperText="Reengaja contato que já conversou mas está inativo há X dias (máx. 20 por dia, uma vez por janela)."
              value={settingsState.crmReactivation || "disabled"}
              onChange={(value) => updateSetting("crmReactivation", value)}
              loading={loading.crmReactivation}
              options={OPTIONS.crmReactivation}
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DebouncedTextField
              label="Dias de inatividade"
              value={settingsState.crmReactivationDays || ""}
              onSave={(value) => updateSetting("crmReactivationDays", value)}
              loading={loading.crmReactivationDays}
              helperText="Padrão: 90 dias."
              type="number"
            />
          </Grid>
          <Grid item xs={12} sm={3}>
            <DebouncedTextField
              label="Mensagem de reativação"
              value={settingsState.crmReactivationTemplate || ""}
              onSave={(value) => updateSetting("crmReactivationTemplate", value)}
              loading={loading.crmReactivationTemplate}
              helperText="Variável: {nome}. Vazio usa o padrão."
              multiline
              rows={2}
            />
          </Grid>
        </Grid>

        <Divider className={classes.sectionDivider} />

        {/* Grupo: Sistema */}
        <Typography className={classes.sectionTitle}>Sistema</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <SelectOption
              className={classes.selectContainer}
              label="Controle de expediente"
              helperText="Define se o horário de atendimento é controlado por fila ou pela empresa."
              value={settingsState.scheduleType || "disabled"}
              onChange={(value) => updateSetting("scheduleType", value)}
              loading={loading.scheduleType}
              options={OPTIONS.scheduleType}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <SelectOption
              className={classes.selectContainer}
              label="Ignorar mensagens de grupos"
              helperText="Quando ativado, mensagens enviadas em grupos do WhatsApp são ignoradas."
              value={settingsState.CheckMsgIsGroup || "disabled"}
              onChange={(value) => updateSetting("CheckMsgIsGroup", value)}
              loading={loading.CheckMsgIsGroup}
              options={OPTIONS.CheckMsgIsGroup}
            />
          </Grid>
        </Grid>

        <Divider className={classes.sectionDivider} />

        {/* Grupo: Agendamentos */}
        <Typography className={classes.sectionTitle}>Agendamentos</Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={6}>
            <SelectOption
              className={classes.selectContainer}
              label="Feedback pós-atendimento"
              helperText="Envia mensagem automática ao cliente 30 min após o término previsto do agendamento."
              value={settingsState.appointmentFeedback || "disabled"}
              onChange={(value) => updateSetting("appointmentFeedback", value)}
              loading={loading.appointmentFeedback}
              options={OPTIONS.appointmentFeedback}
            />
          </Grid>

          {settingsState.appointmentFeedback === "enabled" && (
            <Grid item xs={12}>
              <DebouncedTextField
                label="Mensagem de feedback"
                value={settingsState.appointmentFeedbackMessage || DEFAULT_FEEDBACK_MESSAGE}
                onSave={(value) => updateSetting("appointmentFeedbackMessage", value)}
                loading={loading.appointmentFeedbackMessage}
                helperText="Use {{name}} para o nome do cliente e {{service}} para o serviço agendado."
                multiline
                rows={5}
              />
              <Box display="flex" justifyContent="flex-end" mt={0.5}>
                <Button
                  size="small"
                  color="primary"
                  onClick={() => updateSetting("appointmentFeedbackMessage", DEFAULT_FEEDBACK_MESSAGE)}
                >
                  Restaurar mensagem padrão
                </Button>
              </Box>
            </Grid>
          )}
        </Grid>
      </TabPanel>

      {/* Aba Integrações */}
      <TabPanel value={tabIndex} index={1}>
        <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
          Integrações com sistemas externos para automação de atendimento. Quando configuradas, permitem que clientes recebam informações de cobrança automaticamente ao enviar o CPF/CNPJ pelo WhatsApp. Os campos são salvos automaticamente após parar de digitar.
        </Typography>

        {/* IXC */}
        <Paper variant="outlined" className={classes.integrationCard}>
          <Typography variant="subtitle2" className={classes.integrationTitle}>
            IXC Soft
            <Tooltip
              title="Sistema de gestão para provedores de internet (ISP). Quando configurado, permite que clientes solicitem 2ª via de boleto via WhatsApp enviando o CPF/CNPJ — o sistema consulta o IXC, retorna o valor, vencimento, código de barras e PIX, e pode realizar desbloqueio temporário de conexão."
              placement="top"
              arrow
            >
              <InfoOutlinedIcon className={classes.infoIcon} />
            </Tooltip>
          </Typography>
          <Typography className={classes.integrationDesc}>
            Integração com o sistema IXC para provedores de internet. Ativa a automação de 2ª via de boleto e desbloqueio de conexão via WhatsApp.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <DebouncedTextField
                label="IP / URL do servidor IXC"
                value={settingsState.ipixc}
                onSave={(value) => updateSetting("ipixc", value)}
                loading={loading.ipixc}
                helperText="Endereço do servidor IXC. Ex: http://192.168.1.100 ou http://ixc.suaempresa.com.br"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <DebouncedTextField
                label="Token de autenticação IXC"
                value={settingsState.tokenixc}
                onSave={(value) => updateSetting("tokenixc", value)}
                loading={loading.tokenixc}
                helperText="Token gerado no painel do IXC em Configurações > Integrações > WebService."
              />
            </Grid>
          </Grid>
        </Paper>

        {/* MK-AUTH */}
        <Paper variant="outlined" className={classes.integrationCard}>
          <Typography variant="subtitle2" className={classes.integrationTitle}>
            MK-Auth
            <Tooltip
              title="Sistema de gestão para provedores de internet (ISP). Funciona de forma similar ao IXC: consulta dados do cliente por CPF/CNPJ, gera boleto em PDF, envia código de barras e PIX, e realiza desbloqueio temporário. Usa autenticação OAuth2. Verificado antes do IXC quando ambos estão configurados."
              placement="top"
              arrow
            >
              <InfoOutlinedIcon className={classes.infoIcon} />
            </Tooltip>
          </Typography>
          <Typography className={classes.integrationDesc}>
            Integração com o sistema MK-Auth para provedores de internet. Tem prioridade sobre o IXC quando ambos estão configurados.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={4}>
              <DebouncedTextField
                label="IP / URL do servidor MK-Auth"
                value={settingsState.ipmkauth}
                onSave={(value) => updateSetting("ipmkauth", value)}
                loading={loading.ipmkauth}
                helperText="Endereço do servidor MK-Auth. Ex: http://192.168.1.200 ou http://mk.suaempresa.com.br"
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DebouncedTextField
                label="Client ID"
                value={settingsState.clientidmkauth}
                onSave={(value) => updateSetting("clientidmkauth", value)}
                loading={loading.clientidmkauth}
                helperText="ID de cliente OAuth2 configurado no painel do MK-Auth."
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <DebouncedTextField
                label="Client Secret"
                value={settingsState.clientsecretmkauth}
                onSave={(value) => updateSetting("clientsecretmkauth", value)}
                loading={loading.clientsecretmkauth}
                helperText="Senha de cliente OAuth2 configurada no painel do MK-Auth."
              />
            </Grid>
          </Grid>
        </Paper>

        {/* ASAAS */}
        <Paper variant="outlined" className={classes.integrationCard}>
          <Typography variant="subtitle2" className={classes.integrationTitle}>
            Asaas
            <Tooltip
              title="Plataforma de cobrança online (fintech brasileira). Permite que clientes solicitem 2ª via de boleto enviando o CPF/CNPJ — o sistema busca cobranças em aberto ou vencidas no Asaas e retorna valor, vencimento, código de barras e PIX automaticamente. Verificado após IXC e MK-Auth."
              placement="top"
              arrow
            >
              <InfoOutlinedIcon className={classes.infoIcon} />
            </Tooltip>
          </Typography>
          <Typography className={classes.integrationDesc}>
            Integração com a plataforma de cobranças Asaas. Automatiza o envio de boletos e PIX via WhatsApp quando o cliente envia o CPF/CNPJ.
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <DebouncedTextField
                label="Token de API do Asaas"
                value={settingsState.asaas}
                onSave={(value) => updateSetting("asaas", value)}
                loading={loading.asaas}
                helperText="Chave de API encontrada no painel do Asaas em Configurações > Integrações > Chave de API."
              />
            </Grid>
          </Grid>
        </Paper>
      </TabPanel>
    </Paper>
  );
}
